import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useConfirmDialog } from '@/components/useConfirmDialog';

const initialForm = { nome: '', valor: '', periodicidade: 'mensal', descricao: '' };

export default function PlanosPlataformaPage() {
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    api.get('/admin/planos-plataforma').then(r => setPlanos(r.data)).catch(() => toast.error('Erro ao carregar planos')).finally(() => setLoading(false));
  }, []);

  const refresh = async () => { const r = await api.get('/admin/planos-plataforma'); setPlanos(r.data); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, valor: parseFloat(form.valor), dia_vencimento: parseInt(form.dia_vencimento) || 10 };
      if (editing) {
        await api.put(`/admin/planos-plataforma/${editing.id}`, payload);
        toast.success('Plano atualizado');
      } else {
        await api.post('/admin/planos-plataforma', payload);
        toast.success('Plano criado');
      }
      setDialogOpen(false);
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Remover Plano', 'Tem certeza que deseja remover este plano?')) return;
    try { await api.delete(`/admin/planos-plataforma/${id}`); toast.success('Plano removido'); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const openCreate = () => { setEditing(null); setForm(initialForm); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ nome: p.nome, valor: String(p.valor), periodicidade: p.periodicidade, dia_vencimento: String(p.dia_vencimento || 10), descricao: p.descricao || '' }); setDialogOpen(true); };

  const periodoLabel = { mensal: 'Mensal', trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual' };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="planos-plataforma-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">PLANOS DA PLATAFORMA</h1>
        <Button onClick={openCreate} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="add-plano-button"><Plus className="w-4 h-4 mr-2" /> Novo Plano</Button>
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">Nome</TableHead>
                <TableHead className="text-zinc-400">Valor</TableHead>
                <TableHead className="text-zinc-400">Periodicidade</TableHead>
                <TableHead className="text-zinc-400">Dia Venc.</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400 text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Carregando...</TableCell></TableRow>
              ) : planos.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">Nenhum plano cadastrado</TableCell></TableRow>
              ) : planos.map(p => (
                <TableRow key={p.id} className="border-[#27272A] hover:bg-[#1C1C1E]">
                  <TableCell className="text-white font-medium">{p.nome}</TableCell>
                  <TableCell className="text-zinc-300">R$ {p.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-zinc-400">{periodoLabel[p.periodicidade] || p.periodicidade}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-md font-medium ${p.ativo ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`}>{p.ativo ? 'Ativo' : 'Inativo'}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-zinc-400 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">{editing ? 'Editar Plano' : 'Novo Plano'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome</Label><Input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor (R$)</Label><Input type="number" step="0.01" value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Periodicidade</Label>
                <Select value={form.periodicidade} onValueChange={v => setForm({...form, periodicidade: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Dia Vencimento</Label>
                <Input type="number" min="1" max="31" value={form.dia_vencimento} onChange={e => setForm({...form, dia_vencimento: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="plano-dia-vencimento" />
              </div>
            </div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Descricao</Label><Textarea value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar' : 'Criar')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
}
